import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CrossCloseIcon, MenuIcon } from '@common/utils/icons/icons';
import SphereLogo from '../../media/sphereFullLogo.png';
import { navigationOpenStorageName } from './MainNavigationBar';

/**
 * Application shell shared by the public (logged out) pages and the fleet management pages:
 * a top bar holding the Sphere logo and page actions, a collapsable side navigation bar and a
 * content area filling the remaining space.
 */
const SideNavLayout: React.FC<{
  children: React.ReactNode;
  /** Rendered with the current navigation state so the caller can supply its own navigation bar. */
  renderNavigationBar: (isNavOpen: boolean, onNavChangeHandler: () => void) => React.ReactNode;
  /** Controls rendered on the right hand side of the top bar. */
  actions?: React.ReactNode;
  /** Branding rendered on the left hand side of the top bar, the Sphere logo by default. */
  logo?: React.ReactNode;
  dataTestId?: string;
}> = ({ children, renderNavigationBar, actions, logo, dataTestId }) => {
  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [isNavOpen, setIsNavOpen] = useState(
    localStorage.getItem(navigationOpenStorageName) === 'true',
  );

  const navBarChangeHandler = () => {
    setIsNavOpen((prevState) => {
      localStorage.setItem(navigationOpenStorageName, (!prevState).toString());
      return !prevState;
    });
  };

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        height: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      })}
    >
      <Stack
        sx={(theme) => ({
          backgroundColor: matchesSmDown ? theme.palette.background.paper : theme.palette.background.default,
          boxShadow: matchesSmDown ? '0 3px 5px -1px rgba(0,0,0,0.2)' : undefined,
          zIndex: 1,
          flexShrink: 0,
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={(theme) => ({
            px: 1,
            py: 1,
            minHeight: theme.spacing(8),
          })}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            {matchesSmDown && (
              <Box mr={1}>
                <IconButton
                  onClick={navBarChangeHandler}
                  aria-label={isNavOpen ? 'label-close-navigation-menu' : 'label-open-navigation-menu'}
                  aria-expanded={isNavOpen}
                  aria-controls="sidenav-mobile-navigation-menu"
                >
                  {isNavOpen ? (
                    <CrossCloseIcon sx={{ fontSize: 28 }} />
                  ) : (
                    <MenuIcon sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
              </Box>
            )}
            {logo ?? (!matchesSmDown &&
              <Box
                component="img"
                src={SphereLogo}
                alt="KEMARO Sphere"
                data-testid="sphere-logo"
                sx={(theme) => ({ height: theme.spacing(4) })}
              />
            )}
          </Stack>

          {actions != null && (
            <Stack direction="row" alignItems="center" spacing={2}>
              {actions}
            </Stack>
          )}
        </Stack>

        {matchesSmDown && (
          <Collapse id="sidenav-mobile-navigation-menu" in={isNavOpen}>
            <Box
              sx={(theme) => ({
                maxHeight: `calc(60vh - ${theme.spacing(8)})`,
                overflowY: 'auto',
              })}
            >
              {renderNavigationBar(isNavOpen, navBarChangeHandler)}
            </Box>
          </Collapse>
        )}
      </Stack>

      <Stack direction="row" sx={{ height: '100%', overflow: 'hidden', width: '100%' }}>
        {!matchesSmDown && renderNavigationBar(isNavOpen, navBarChangeHandler)}
        <Stack
          sx={{
            width: '100%',
            overflow: 'hidden',
            height: '100%',
            pr: { xs: 0, sm: 1 },
            pb: { xs: 0, sm: 1 },
          }}
        >
          <Box
            sx={(theme) => ({
              overflowY: 'auto',
              height: '100%',
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.spacing(1),
            })}
          >
            {children}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SideNavLayout;
